/**
 * OS's filler in live mode (docs/plans/os-voice-conversation.md, J10 as
 * revised by Devendra on 7 Oct: acknowledgements are not shown or stored).
 *
 * While Coach works the voice may say one short line ("Ek second", "Okay,
 * one moment"). It is heard, and the live caption says Working, but it is no
 * bubble in the chat and no message in the thread: the thread holds what was
 * asked and what was answered. The same goes for a line the voice says to
 * fill a wait ("Still waiting on the final details.", "Checking…", "dekhti
 * hoon") whatever kind it opened as, and for a line with no words in it at
 * all ("…", "{pause}"). Pure, for the server and both clients alike.
 */

/** A line has something to say: a letter or a digit, in any script. */
export const hasSpokenWords = (text: string): boolean => /[\p{L}\p{N}]/u.test(text);

/** How many words a filler line has at most: a longer one is saying something. */
export const FILLER_MAX_WORDS = 12;

/**
 * What marks a line as filling a wait, matched on its folded words: English,
 * Hinglish and Hindi. A short line with one of these says nothing but that OS
 * is busy.
 */
const WAITING_MARKS: readonly RegExp[] = [
  /\bstill (waiting|working|checking|looking|on it)\b/u,
  /\bwaiting (for|on)\b/u,
  /\b(one|a|just a) (moment|second|sec|minute|min)\b/u,
  /\bgive me a (moment|second|sec|minute)\b/u,
  /\b(hold on|hang on|bear with me)\b/u,
  /\b(let me|i will|i'll) (check|see|look|get|find)\b/u,
  /\bi('m| am) (checking|looking|getting|working|on it|finding|pulling|adding|updating|fetching)\b/u,
  /\b(checking|looking into it|working on it|on it)\b/u,
  /\b(updating|adding|getting|pulling up|fetching|preparing|setting up) (that|it|this|the|your)\b/u,
  /\btaking (a (little|bit) )?(longer|a while|some time)\b/u,
  /\b(ek|bas ek) (second|sec|minute|min|minat|pal)\b/u,
  /\b(dekh|dekhti|dekhta) (rahi|raha|hoon|hun|hu|leti|leta)\b/u,
  /\b(dekhti|dekhta|batati|batata) (hoon|hun|hu)\b/u,
  /\b(ruko|rukiye|zara ruko|ruk jao)\b/u,
  /एक (सेकंड|सेकेंड|मिनट|पल)/u,
  /(देख|देखती|देखता) (रही|रहा|हूँ|हूं)/u,
  /(रुको|रुकिए|ज़रा|जरा)/u,
];

/** The words of a line, folded: lower case, curly quotes straightened, punctuation gone. */
const folded = (text: string): string =>
  text
    .normalize('NFC')
    .toLowerCase()
    .replace(/[’‘]/gu, "'")
    .replace(/[^\p{L}\p{M}\p{N}'\s]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();

/** A line that only fills a wait: short, and saying OS is busy or still waiting. */
export const isWaitingLine = (text: string): boolean => {
  const words = folded(text);
  if (words === '' || words.split(' ').length > FILLER_MAX_WORDS) return false;
  return WAITING_MARKS.some((mark) => mark.test(words));
};

/**
 * Whether one of OS's lines is drawn and kept in the thread: never an
 * acknowledgement, never the spoken gist of a Coach answer (the answer is
 * already in the thread; Devendra, 12 Oct: Coach read its gists and wrote the
 * same answer three times), never a line with no words, and never a short
 * line that only fills a wait — unless it is the greeting or a fixed line
 * the app asked for, which always are. Small talk with no answer under it
 * (`say`) is kept.
 */
export const isThreadLine = (kind: string | null | undefined, text: string): boolean => {
  if (!hasSpokenWords(text)) return false;
  if (kind === 'ack' || kind === 'gist') return false;
  if (kind === 'greeting' || kind === 'fixed') return true;
  return !isWaitingLine(text);
};
